import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { ehStatusEquipamento } from '@/lib/dominio';
import { formatarAteDuasCasas, formatarInteiro, formatarReais } from '@/lib/formato';
import { retornoDe, semContexto, type ContextoDoRetorno } from '@/lib/retorno';
import { ehFichaAlterada } from '@/lib/versao-da-ficha';

import {
  api,
  type AbastecimentoDaApi,
  type CadastrosDaApi,
  type CobrancaDaApi,
  type EnvioDeAbastecimento,
  type EnvioDeCobranca,
  type EnvioDeLubrificante,
  type EnvioDeMedicao,
  type EnvioDeOrdem,
  type EnvioDeRecebimento,
  type EquipamentoDaApi,
  type LubrificanteDaApi,
  type OrdemDaApi,
  SemSessao,
  type TanqueDaApi,
} from './api';
import type {
  PostoCadastradoDaApi,
  ProdutoCadastradoDaApi,
  TanqueCadastradoDaApi,
} from './api';
import { separarEstornados } from './estorno';
import { faixaDe } from './tipos';
import type {
  Abastecimento,
  AplicacaoDeLubrificante,
  Cobranca,
  EquipamentoDoModulo,
  Ordem,
  SaldoDeTanque,
  Tanque,
} from './tipos';

/**
 * A única porta de entrada de dados do módulo 08.
 *
 * ## UMA fonte, e ela é o banco
 *
 * Este arquivo tinha duas. A API existia e gravava de verdade, mas não havia
 * autenticação: sem cookie, toda rota respondia 401, e cada consulta caía numa
 * massa de demonstração para a tela ter o que mostrar.
 *
 * Agora há entrada de verdade, e a queda saiu inteira — `demonstracao.ts` não
 * existe mais, `Carga` não existe mais, e nenhuma tela deste módulo tem como
 * exibir um número que não esteja no banco. Quem não tem sessão não chega até
 * aqui: a casca da aplicação para antes e diz o que fazer, em vez de desenhar
 * uma lista vazia que faria parecer que a empresa não tem máquina.
 *
 * Recusa COM MOTIVO (400, 409, 422) continua subindo até o formulário: é o
 * banco dizendo que o lançamento está errado, e a pessoa pode corrigi-lo.
 */

/* ==========================================================================
 * Do JSON da API para o domínio do módulo
 * ======================================================================= */

function instante(iso: string): Date {
  return new Date(iso);
}

function instanteOuNulo(iso: string | null): Date | null {
  return iso === null ? null : new Date(iso);
}

/**
 * A linha da API no domínio do módulo. Exportada para o celular (27/10/2026):
 * a folha do abastecimento lê a mesma ficha do painel da mesa
 * (`fichaDoAbastecimento`), e a ficha lê o domínio.
 */
export function mapearAbastecimento(l: AbastecimentoDaApi): Abastecimento {
  return {
    id: l.id,
    equipamentoId: l.equipamentoId,
    equipamentoCodigo: l.equipamentoCodigo,
    quando: instante(l.ocorridoEm),
    litros: l.quantidadeL,
    precoLitro: l.precoLitro,
    horimetro: l.medidor,
    obra: l.obra,
    tanque: l.tanque,
    operador: l.operador,
    responsavel: l.responsavel,
    centroCusto: l.centroCusto,
    tipo: l.tipo,
    posto: l.posto,
    comprovante: l.comprovante,
    produto: l.produto,
    produtoId: l.produtoId,
    produtoCategoria: l.produtoCategoria,
    ordemNumero: l.ordemNumero,
    valorTotal: l.valorTotal,
    consumoDoBanco: l.consumoPorUnidade,
    limitacao: l.limitacao,
    foraDaFaixa: l.foraDaFaixa,
    litrosNoIntervalo: l.litrosNoIntervalo,
    abastecimentosSemLeitura: l.abastecimentosSemLeitura,
    variacaoDoMedidor: l.variacaoDoMedidor,
    tipoMedidor: l.tipoMedidor,
    ids: {
      tanqueId: l.tanqueId ?? null,
      postoId: l.postoId ?? null,
      ordemId: l.ordemId ?? null,
      obraId: l.obraId ?? null,
      centroCustoId: l.centroCustoId ?? null,
      operadorId: l.operadorId ?? null,
      responsavelId: l.responsavelId ?? null,
      observacao: l.observacao,
    },
    estorno: l.estornadoEm ? { em: instante(l.estornadoEm), motivo: l.motivoDoEstorno ?? null, por: l.estornadoPor ?? null } : null,
    corrigeId: l.corrigeId ?? null,
    corrigidoPorId: l.corrigidoPorId ?? null,
    /* 0122 · a conferência chega inteira ou não chega: o banco garante o par
       pelo `check ((conferido_em is null) = (conferido_por is null))`, e o
       nome é nulo só quando o usuário foi apagado depois. */
    conferencia: l.conferidoEm ? { em: instante(l.conferidoEm), por: l.conferidoPorNome ?? null } : null,
    doCelular: l.doCelular ?? false,
    daFolha: l.daFolha ?? false,
    equipamentoDescricao: l.equipamentoDescricao ?? null,
    equipamentoTipo: l.equipamentoTipo,
    /* QUEM REGISTROU vem da trilha (27/10/2026), e é por isso que ele NÃO se
       completa com o operador nem com o responsável quando falta: os dois
       são escolhas de quem lança, e um "lançado por" preenchido com eles
       atribuiria o registro a quem não registrou. */
    registro: {
      em: l.registradoEm ? instante(l.registradoEm) : null,
      por: l.registradoPor ?? null,
      perfil: l.registradoPorPerfil ?? null,
    },
  };
}

function mapearEquipamento(l: EquipamentoDaApi): EquipamentoDoModulo {
  /* A faixa chega JÁ RESOLVIDA pelo servidor: desde a decisão 8 do dono ela é
     da UNIDADE, e a da configuração vale como herança quando a unidade não
     declara a sua (`ativos.equipamento_efetivo`, migração 0011). A tela não
     escolhe entre as duas — escolher de novo aqui seria uma segunda regra de
     herança, e duas regras de herança divergem na primeira correção. */
  /* BASTA UMA DAS PONTAS. O banco aceita a faixa pela metade de propósito — o
     gestor que só se importa com desperdício cadastra o teto e deixa o piso em
     branco — e `comb.consumo_abastecimento` julga essa faixa normalmente.
     Exigir o par aqui descartava justamente a faixa que o servidor ia usar. */
  const cadastrada = faixaDe(l.consumoMinimo, l.consumoMaximo);

  return {
    id: l.id,
    codigo: l.codigo,
    tipo: l.tipo,
    modelo: l.modelo,
    status: ehStatusEquipamento(l.status) ? l.status : null,
    motivo: l.motivo,
    obraId: l.obraId,
    obra: l.obra,
    centroCustoId: l.centroCustoId,
    centroCusto: l.centroCusto,
    tipoMedidor: l.tipoMedidor,
    ultimaLeitura: l.ultimaLeitura,
    ultimaLeituraEm: l.ultimaLeituraEm ? new Date(l.ultimaLeituraEm) : null,
    combustivel: l.combustivel ?? null,
    ultimoProdutoId: l.ultimoProdutoId ?? null,
    codigoInterno: l.codigoInterno ?? null,
    placa: l.placa ?? null,
    /* A FAIXA SÓ VEM DO CADASTRO. Ela tinha uma segunda fonte — uma tabela
       por tipo de máquina, com números inventados, que valia quando a
       configuração não declarava a faixa. Agora que a configuração é cadastro
       de verdade, a segunda fonte saiu: um aviso de "consumo fora do esperado"
       calculado sobre número inventado acusa a máquina errada, e quem o vir
       uma vez não confia nele de novo. Sem faixa cadastrada não há aviso. */
    faixa: cadastrada,
    /* Já chegava da API e morria aqui: é contra ela que o lançamento guiado
       confere se o volume cabe no tanque da máquina. */
    capacidadeTanqueL: l.capacidadeTanqueL,
  };
}

function mapearTanque(l: TanqueDaApi): SaldoDeTanque {
  return {
    id: l.id,
    codigo: l.codigo,
    nome: l.nome,
    tipo: l.tipo,
    produtoId: l.produtoId,
    produto: l.produto,
    padrao: l.padrao,
    capacidadeL: l.capacidadeL,
    saldoCalculadoL: l.saldoCalculadoL,
    toleranciaL: l.toleranciaL,
    precoLitro: l.precoLitro,
    obra: l.obra,
    medidoEm: instanteOuNulo(l.ultimaMedicaoEm),
    medicaoObservadaL: l.ultimaMedicaoObservadoL,
    medicaoCalculadaL: l.ultimaMedicaoCalculadoL,
    diferencaL: l.ultimaMedicaoDiferencaL,
    dentroDaTolerancia: l.dentroDaTolerancia,
  };
}

function mapearOrdem(l: OrdemDaApi): Ordem {
  return {
    id: l.id,
    numero: l.numero,
    situacao: l.situacao,
    autorizadaEm: instante(l.autorizadaEm),
    validadeEm: instanteOuNulo(l.validadeEm),
    equipamentoId: l.equipamentoId,
    equipamentoCodigo: l.equipamentoCodigo,
    tanqueId: l.tanqueId,
    tanque: l.tanque,
    autorizadoL: l.quantidadeAutorizadaL,
    entregueL: l.quantidadeEntregueL,
    saldoL: l.saldoAutorizadoL,
    abastecimentos: l.abastecimentos,
    autorizadoPor: l.autorizadoPor,
    motivoCancelamento: l.motivoCancelamento,
  };
}

function mapearCobranca(l: CobrancaDaApi): Cobranca {
  return {
    id: l.id,
    documento: l.documento,
    competencia: l.competencia,
    situacao: l.situacao,
    fornecedor: l.fornecedor,
    posto: l.posto,
    valorCobrado: l.valorCobrado,
    valorLancado: l.valorLancado,
    diferenca: l.diferenca,
    abastecimentos: l.abastecimentos,
    litros: l.litros,
  };
}

function mapearLubrificante(l: LubrificanteDaApi): AplicacaoDeLubrificante {
  return {
    id: l.id,
    quando: instante(l.ocorridoEm),
    equipamentoCodigo: l.equipamentoCodigo,
    produto: l.produto,
    quantidade: l.quantidade,
    precoUnitario: l.precoUnitario,
    valorTotal: l.valorTotal,
    unidade: l.unidade,
    ponto: l.ponto,
    responsavel: l.responsavel,
    medidor: l.medidor,
  };
}

/* ==========================================================================
 * Os cadastros que o formulário precisa
 * ======================================================================= */

/**
 * Tudo o que o formulário de lançamento pergunta, numa ida só ao servidor.
 *
 * Em oito consultas separadas a tela montaria em oito estados intermediários,
 * cada um deles um jeito de mostrar meia verdade — o equipamento já escolhido e
 * a lista de tanques ainda vazia, por exemplo.
 */
export interface CadastrosDoLancamento {
  readonly equipamentos: readonly EquipamentoDoModulo[];
  readonly tanques: readonly SaldoDeTanque[];
  readonly postos: readonly { readonly id: string; readonly nome: string; readonly municipio: string }[];
  readonly produtos: readonly {
    readonly id: string;
    readonly nome: string;
    readonly categoria: string;
    readonly unidade: string;
  }[];
  readonly obras: readonly {
    readonly id: string;
    readonly nome: string;
    readonly centroCustoId: string | null;
    readonly centroCusto: string | null;
  }[];
  readonly centrosCusto: readonly { readonly id: string; readonly nome: string }[];
  readonly fornecedores: readonly { readonly id: string; readonly nome: string }[];
  readonly operadores: readonly { readonly id: string; readonly nome: string }[];
  readonly responsaveis: readonly { readonly id: string; readonly nome: string }[];
}

/** Exportada para o pacote do combustivel guardado no aparelho (0129) ser lido pelo MESMO conversor. */
export function converterCadastros(c: CadastrosDaApi): CadastrosDoLancamento {
  return {
    equipamentos: c.equipamentos.map(mapearEquipamento),
    tanques: c.tanques.map(mapearTanque),
    postos: c.postos.map((p) => ({ id: p.id, nome: p.nome, municipio: p.municipio })),
    produtos: c.produtos.map((p) => ({ id: p.id, nome: p.nome, categoria: p.categoria, unidade: p.unidade })),
    obras: c.obras.map((o) => ({
      id: o.id,
      nome: o.nome,
      centroCustoId: o.centroCustoId,
      centroCusto: o.centroCusto,
    })),
    centrosCusto: c.centrosCusto.map((x) => ({ id: x.id, nome: x.nome })),
    fornecedores: c.fornecedores.map((x) => ({ id: x.id, nome: x.nome })),
    /* Operador e responsável saem da MESMA tabela de usuários: quem opera a
       máquina e quem libera o diesel são papéis diferentes da mesma pessoa
       cadastrada. Separá-los em duas listas é decisão do módulo 01 (perfis e
       alçadas), e está registrada como pendência. */
    operadores: c.usuarios.map((x) => ({ id: x.id, nome: x.nome })),
    responsaveis: c.usuarios.map((x) => ({ id: x.id, nome: x.nome })),
  };
}

const CHAVE = ['combustivel'] as const;

/**
 * OS CADASTROS FICAM NA MEMORIA DO APP enquanto ele estiver aberto (`gcTime`
 * sem fim), e o celular os pede ao abrir, com sinal (`prepararCadastrosDoLancamento`):
 * o operador que perde o sinal com o app aberto ainda lanca o abastecimento —
 * o formulario so precisa da lista de maquinas e tanques (robos contra o dist,
 * 20/10/2026: "sem rede, o formulario fica Carregando se nao foi aberto antes
 * com sinal").
 */
const CONSULTA_DOS_CADASTROS = {
  queryKey: [...CHAVE, 'cadastros'],
  queryFn: async () => converterCadastros(await api.cadastros()),
  gcTime: Infinity,
  /* A tela de lancamento fica aberta o turno inteiro no posto: sem isto, a
     maquina marcada na ficha depois que ela abriu nao entrava na lista ate
     recarregar (dono, 31/10/2026). Volta a pedir quando a tela volta a ser
     usada, passados os 30 s de sempre. */
  refetchOnWindowFocus: true,
} as const;

export function useCadastros() {
  return useQuery(CONSULTA_DOS_CADASTROS);
}

/** Pede os cadastros do lançamento antes de alguém abrir o formulário (ver acima). */
export function prepararCadastrosDoLancamento(cliente: QueryClient): void {
  void cliente.prefetchQuery(CONSULTA_DOS_CADASTROS);
}

/* ==========================================================================
 * As listas
 * ======================================================================= */

/**
 * A lista de abastecimentos com o RECORTE ao lado.
 *
 * As telas somam esta lista para escrever volume, gasto e consumo. Ela vem
 * cortada pelo servidor, e quem soma precisa saber disso — senão a soma de 200
 * linhas é apresentada como se fosse a soma de todas. `Recortada` carrega
 * `total` e `limite` junto com as linhas justamente para o indicador conseguir
 * dizer "de N" na mesma tela em que mostra o número.
 */
export interface Recortada<T> {
  readonly linhas: readonly T[];
  readonly total: number;
  readonly limite: number;
  /**
   * 0087 · os estornados, FORA de `linhas`: toda soma do módulo lê `linhas`, e
   * o estornado não conta em volume, gasto, consumo nem pendência (ADR-008).
   * A lista de lançamentos os mostra recolhidos, à parte.
   */
  readonly estornados?: readonly T[];
}

/** `true` quando o servidor devolveu menos do que existe. */
export function foiCortada(lista: Recortada<unknown> | undefined): boolean {
  return lista !== undefined && lista.total > lista.linhas.length;
}

export function useAbastecimentos() {
  return useQuery<Recortada<Abastecimento>>({
    queryKey: [...CHAVE, 'abastecimentos'],
    queryFn: async () => {
      const r = await api.abastecimentos();
      const { vigentes, estornados } = separarEstornados(r.abastecimentos);
      return {
        linhas: vigentes.map(mapearAbastecimento),
        estornados: estornados.map(mapearAbastecimento),
        /* O total do servidor conta os estornados que vieram na lista; eles
           saem da conta junto com as linhas. */
        total: Math.max(r.total - estornados.length, vigentes.length),
        limite: r.limite,
      };
    },
  });
}

/** Os externos que ainda nao foram ligados a cobranca nenhuma. */
export function useExternosSemCobranca() {
  return useQuery<Recortada<Abastecimento>>({
    queryKey: [...CHAVE, 'abastecimentos', 'sem-cobranca'],
    queryFn: async () => {
      const r = await api.abastecimentos({ semCobranca: true });
      return { linhas: r.abastecimentos.map(mapearAbastecimento), total: r.total, limite: r.limite };
    },
  });
}

export function useTanques() {
  return useQuery<readonly SaldoDeTanque[]>({
    queryKey: [...CHAVE, 'tanques'],
    queryFn: async () => (await api.tanques()).tanques.map(mapearTanque),
  });
}

/**
 * `ligada` falso: a pessoa de posto parceiro (0132) lança sem ordem de
 * abastecimento, e o servidor recusa a ela a lista das ordens.
 */
export function useOrdens(ligada = true) {
  return useQuery<readonly Ordem[]>({
    queryKey: [...CHAVE, 'ordens'],
    enabled: ligada,
    queryFn: async () => (await api.ordens()).ordens.map(mapearOrdem),
  });
}

export function useCobrancas() {
  return useQuery<readonly Cobranca[]>({
    queryKey: [...CHAVE, 'cobrancas'],
    queryFn: async () => (await api.cobrancas()).cobrancas.map(mapearCobranca),
  });
}

export function useLubrificantes() {
  return useQuery<Recortada<AplicacaoDeLubrificante>>({
    queryKey: [...CHAVE, 'lubrificantes'],
    queryFn: async () => {
      const r = await api.lubrificantes();
      return { linhas: r.lubrificantes.map(mapearLubrificante), total: r.total, limite: r.limite };
    },
  });
}

/* ==========================================================================
 * O que grava
 * ======================================================================= */

/**
 * Lança o abastecimento — o ÚNICO caminho de escrita deste fato.
 *
 * Duas telas lançam abastecimento (`/combustivel/lancamento` e o formulário
 * dentro de `/combustivel`), e as duas chamam esta mutação — que chama uma
 * rota só. A validação do horímetro, a leitura do medidor, a baixa do tanque e
 * a linha de trilha acontecem uma vez, no servidor, para as duas telas. Um
 * segundo caminho de escrita seria um segundo lugar onde a baixa do tanque
 * pode ser esquecida.
 *
 * Recusa com motivo sobe para o formulário: é a única resposta que permite
 * corrigir e reenviar.
 */
/* ==========================================================================
 * Os cadastros do módulo — tanque, posto e produto
 *
 * Três listas e seis mutações com a MESMA forma, e por isso uma fábrica só. A
 * alternativa era escrever seis hooks quase idênticos, e o sexto esqueceria a
 * invalidação — o sintoma seria uma tela que grava certo e continua mostrando
 * a lista velha até alguém recarregar a página.
 *
 * A chave invalidada é `CHAVE` inteira, e não só a da lista alterada: cadastrar
 * um tanque muda o que o formulário de lançamento oferece, e cadastrar um
 * produto muda o que o formulário de tanque oferece. Invalidar só a própria
 * lista deixaria a outra tela oferecendo um catálogo que envelheceu.
 * ======================================================================= */

export function useTanquesCadastrados(ligada = true) {
  return useQuery({
    queryKey: [...CHAVE, 'cadastros', 'tanques'],
    /* Desligada fora do combustível: a trava do papel (0128), em Papéis, só a
       pede quem pode ler o combustível. */
    enabled: ligada,
    queryFn: async () => (await api.tanquesCadastrados()).itens,
  });
}

export function usePostosCadastrados() {
  return useQuery({
    queryKey: [...CHAVE, 'cadastros', 'postos'],
    queryFn: async () => (await api.postosCadastrados()).itens,
  });
}

export function useProdutosCadastrados() {
  return useQuery({
    queryKey: [...CHAVE, 'cadastros', 'produtos'],
    queryFn: async () => (await api.produtosCadastrados()).itens,
  });
}

const litros = (n: number) => `${formatarAteDuasCasas(n)} L`;

/** O que qualquer um dos três cadastros devolve ao gravar. */
export type CadastroDoModulo = TanqueCadastradoDaApi | PostoCadastradoDaApi | ProdutoCadastradoDaApi;

/** Grava um cadastro: sem `id` é criação, com `id` é alteração do que mudou. */
export function useGravarCadastro(
  assunto: 'tanques' | 'postos' | 'produtos',
) {
  const cliente = useQueryClient();
  const criar = { tanques: api.criarTanque, postos: api.criarPosto, produtos: api.criarProduto }[assunto];
  const alterar = { tanques: api.alterarTanque, postos: api.alterarPosto, produtos: api.alterarProduto }[assunto];

  /* O tipo do resultado e a UNIAO dos tres, e nao o de um deles: sem isso o
     TypeScript escolhe o primeiro ramo do `?:` como tipo de toda a fabrica, e
     quem grava um posto recebe de volta algo tipado como tanque. */
  const objeto = { tanques: 'Tanque', postos: 'Posto', produtos: 'Produto' }[assunto];
  return useMutation<CadastroDoModulo, Error, { id?: string; envio: Record<string, unknown> }>({
    meta: {
      retorno: retornoDe<CadastroDoModulo, { id?: string }>((r, v) => ({
        titulo: `${objeto} ${v.id === undefined ? 'cadastrado' : 'alterado'}`,
        alvo: { codigo: r.codigo, nome: r.nome },
        fatos: [
          'capacidadeL' in r ? litros(r.capacidadeL) : null,
          'produto' in r ? r.produto : null,
          'categoria' in r ? r.categoria : null,
          'municipio' in r && r.municipio ? `${r.municipio}${r.uf ? `/${r.uf}` : ''}` : null,
          r.ativo ? null : 'Arquivado',
        ],
      })),
    },
    mutationFn: ({ id, envio }) => (id === undefined ? criar(envio) : alterar(id, envio)),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
    /* Recusada por versao velha (robos de 20/10/2026): a lista rele, e reabrir mostra a de agora. */
    onError: (erro) => {
      if (ehFichaAlterada(erro)) void cliente.invalidateQueries({ queryKey: CHAVE });
    },
  });
}

export function useLancarAbastecimento() {
  const cliente = useQueryClient();

  return useMutation<Abastecimento, Error, { envio: EnvioDeAbastecimento }>({
    /* O toast de sucesso sai da tela, que sabe a máquina e o consumo; a recusa
       fica no aviso do topo do formulário (p.22). */
    meta: { silencioso: true, silenciarFalha: true },
    mutationFn: async ({ envio }) => {
      const { abastecimento } = await api.lancarAbastecimento(envio);
      return mapearAbastecimento(abastecimento);
    },
    onSuccess: () => {
      /* Sem `exact`: o lançamento novo precisa aparecer na lista, no saldo do
         tanque, na ordem que ele atendeu e no consumo do equipamento. Invalidar
         só a chave exata deixaria três telas mostrando número velho. */
      void cliente.invalidateQueries({ queryKey: CHAVE });
    },
  });
}

/** Em quê e o que valeu, para o retorno do estorno e da correção (R04, p.32). */
export interface ContextoDoAbastecimento {
  readonly contexto?: ContextoDoRetorno;
}

/**
 * ADR-008, 0087: estornar o abastecimento. O efeito depende da origem — o
 * volume volta ao tanque no interno; o externo sai da cobrança ainda aberta —,
 * e é o que o retorno escreve.
 */
export function useEstornarAbastecimento() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<{ abastecimento: AbastecimentoDaApi }, { motivo: string } & ContextoDoAbastecimento>((r, v) => ({
        titulo: 'Abastecimento estornado',
        alvo: v.contexto?.alvo ?? { codigo: r.abastecimento.equipamentoCodigo },
        fatos: [...(v.contexto?.fatos ?? []), `motivo: ${v.motivo}`],
        efeito:
          r.abastecimento.tipo === 'interno'
            ? `${litros(r.abastecimento.quantidadeL)} voltaram a ${r.abastecimento.tanque ?? 'o tanque'}; o lançamento sai do consumo e do custo`
            : 'O lançamento sai da cobrança do posto, do consumo e do custo',
      })),
      silenciarFalha: true,
    },
    mutationFn: ({ id, motivo }: { id: string; motivo: string } & ContextoDoAbastecimento) => api.estornarAbastecimento(id, motivo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/**
 * 0126: APAGAR o lançamento — decisão do dono em 26/10/2026, e só do
 * administrador da empresa.
 *
 * O retorno NÃO nomeia o que voltou ao tanque, como o do estorno faz: a linha
 * não existe mais, e descrevê-la seria contar o que ela era para quem acabou
 * de dizer que ela não deveria ter existido. O que o toast diz é o fato — foi
 * apagado, com o motivo, e o registro guarda a linha inteira.
 */
export function useExcluirAbastecimento() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<{ id: string }, { motivo: string } & ContextoDoAbastecimento>((_r, v) => ({
        titulo: 'Lançamento apagado',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: [...(v.contexto?.fatos ?? []), `motivo: ${v.motivo}`],
        efeito: 'A linha sai da lista; a cópia dela fica no registro de exclusões, com quem apagou',
      })),
      silenciarFalha: true,
    },
    mutationFn: ({ id, motivo }: { id: string; motivo: string } & ContextoDoAbastecimento) =>
      api.excluirAbastecimento(id, motivo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/**
 * 0122: marcar o lançamento como conferido — alguém olhou e assinou embaixo.
 *
 * NÃO tem motivo, e isso é a diferença dela com o estorno e a correção: as
 * duas desfazem algo e precisam dizer por quê; a conferência não desfaz nada.
 * O que ela produz é a assinatura, e a assinatura é o nome de quem conferiu
 * com a hora — que o servidor escreve, nunca a tela.
 *
 * `silenciarFalha` fica DESLIGADO aqui, ao contrário do estorno: o estorno
 * mostra o erro dentro da janela dele, e esta ação não tem janela — o botão
 * está no painel, e o único lugar onde a recusa pode aparecer é o toast.
 */
export function useConferirAbastecimento() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<{ abastecimento: AbastecimentoDaApi }, ContextoDoAbastecimento>((r, v) => ({
        titulo: 'Lançamento conferido',
        alvo: v.contexto?.alvo ?? { codigo: r.abastecimento.equipamentoCodigo },
        fatos: [
          ...(v.contexto?.fatos ?? []),
          ...(r.abastecimento.conferidoPorNome ? [`por ${r.abastecimento.conferidoPorNome}`] : []),
        ],
        efeito: 'O lançamento sai de “Para conferir”; os números dele continuam os mesmos',
      })),
    },
    mutationFn: ({ id }: { id: string } & ContextoDoAbastecimento) => api.conferirAbastecimento(id),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/** 0087: corrigir = estornar o errado e lançar o certo, numa transação. */
export function useCorrigirAbastecimento() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<
        { abastecimento: AbastecimentoDaApi },
        { motivo: string; envio: EnvioDeAbastecimento; antes: number } & ContextoDoAbastecimento
      >((r, v) => ({
        titulo: 'Abastecimento corrigido',
        alvo: v.contexto?.alvo ?? { codigo: r.abastecimento.equipamentoCodigo },
        fatos: [`${litros(v.antes)} → ${litros(r.abastecimento.quantidadeL)}`, ...(v.contexto?.fatos ?? []), `motivo: ${v.motivo}`],
        efeito: 'O lançamento errado foi estornado e o certo entrou no lugar dele',
      })),
      silenciarFalha: true,
    },
    mutationFn: ({ id, motivo, envio }: { id: string; motivo: string; envio: EnvioDeAbastecimento; antes: number } & ContextoDoAbastecimento) =>
      api.corrigirAbastecimento(id, motivo, envio),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useAutorizarOrdem() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<{ numero: number }, EnvioDeOrdem & { contexto?: ContextoDoRetorno }>((r, v) => ({
        titulo: 'Ordem de abastecimento autorizada',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: [litros(v.quantidadeAutorizadaL), ...(v.contexto?.fatos ?? [])],
        codigo: `Ordem ${formatarInteiro(r.numero)}`,
      })),
      silenciarFalha: true,
    },
    mutationFn: (v: EnvioDeOrdem & { contexto?: ContextoDoRetorno }) => api.autorizarOrdem(semContexto(v)),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useCancelarOrdem() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { contexto?: ContextoDoRetorno }>((_r, v) => ({
        titulo: 'Ordem de abastecimento cancelada',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: v.contexto?.fatos ?? [],
      })),
    },
    mutationFn: ({ id, motivo }: { id: string; motivo: string; contexto?: ContextoDoRetorno }) => api.cancelarOrdem(id, motivo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useMedirTanque() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { envio: EnvioDeMedicao; contexto?: ContextoDoRetorno }>((_r, v) => ({
        titulo: 'Medição registrada',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: [`${litros(v.envio.volumeObservadoL)} na régua`],
      })),
    },
    mutationFn: ({ id, envio }: { id: string; envio: EnvioDeMedicao; contexto?: ContextoDoRetorno }) => api.medirTanque(id, envio),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useReceberNoTanque() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { envio: EnvioDeRecebimento; contexto?: ContextoDoRetorno }>((_r, v) => ({
        titulo: 'Recebimento lançado',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: [
          litros(v.envio.quantidadeL),
          ...(v.contexto?.fatos ?? []),
          v.envio.precoLitro === undefined ? null : `${formatarReais(v.envio.precoLitro)}/L`,
          v.envio.documento ? `documento ${v.envio.documento}` : null,
        ],
      })),
      silenciarFalha: true,
    },
    mutationFn: ({ id, envio }: { id: string; envio: EnvioDeRecebimento; contexto?: ContextoDoRetorno }) => api.receberNoTanque(id, envio),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/**
 * O livro de um tanque. Só busca quando há tanque escolhido.
 */
export function useMovimentosDoTanque(tanqueId: string | null) {
  return useQuery({
    queryKey: [...CHAVE, 'movimentos', tanqueId],
    queryFn: async () => (await api.movimentosDoTanque(tanqueId!)).movimentos,
    enabled: tanqueId !== null,
  });
}

/**
 * ADR-008. Invalida a chave inteira do módulo, e não só o livro: o estorno
 * muda o saldo do tanque, a soma da faixa de indicadores e a contagem de
 * tanques fora da tolerância. Invalidar só a lista deixaria o número do topo
 * contradizendo a linha que a pessoa acabou de anular.
 */
export function useEstornarMovimento() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { motivo: string; contexto?: ContextoDoRetorno }>((_r, v) => ({
        titulo: 'Lançamento estornado',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: [...(v.contexto?.fatos ?? []), `motivo: ${v.motivo}`],
        efeito: 'O contra-lançamento entrou nos lançamentos do tanque',
      })),
      silenciarFalha: true,
    },
    mutationFn: ({ id, motivo }: { id: string; motivo: string; contexto?: ContextoDoRetorno }) => api.estornarMovimento(id, motivo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/* 0059 · os pedidos de ajuste. Toda mutação invalida a chave inteira do
   módulo, pelo mesmo motivo do estorno: a aprovação muda o volume do tanque,
   a faixa de indicadores e o livro de uma vez. */
export function useAjustes() {
  return useQuery({
    queryKey: [...CHAVE, 'ajustes'],
    queryFn: async () => (await api.ajustes()).ajustes,
  });
}

export function usePedirCorrecao() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { envio: Parameters<typeof api.pedirCorrecao>[1]; contexto?: ContextoDoRetorno }>((_r, v) => ({
        titulo: 'Correção pedida',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: [...(v.contexto?.fatos ?? []), `${litros(v.envio.quantidadeCorretaL)} corretos`],
        efeito: 'O volume só muda quando alguém com alçada de ajuste aprovar',
      })),
      silenciarFalha: true,
    },
    mutationFn: ({ id, envio }: { id: string; envio: Parameters<typeof api.pedirCorrecao>[1]; contexto?: ContextoDoRetorno }) =>
      api.pedirCorrecao(id, envio),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function usePedirBalanco() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { envio: Parameters<typeof api.pedirBalanco>[1]; contexto?: ContextoDoRetorno }>((_r, v) => ({
        titulo: 'Balanço pedido',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: [`${litros(v.envio.volumeContadoL)} contados`],
        efeito: 'O volume do tanque só muda quando alguém com alçada de ajuste aprovar',
      })),
      silenciarFalha: true,
    },
    mutationFn: ({ id, envio }: { id: string; envio: Parameters<typeof api.pedirBalanco>[1]; contexto?: ContextoDoRetorno }) =>
      api.pedirBalanco(id, envio),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useAprovarAjuste() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { nota?: string; contexto?: ContextoDoRetorno }>((_r, v) => ({
        titulo: 'Ajuste aprovado',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: [...(v.contexto?.fatos ?? []), v.nota ? `nota: ${v.nota}` : null],
        efeito: 'Os lançamentos do tanque já mostram a correção',
      })),
      silenciarFalha: true,
    },
    mutationFn: ({ id, nota }: { id: string; nota?: string; contexto?: ContextoDoRetorno }) => api.aprovarAjuste(id, nota),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useRecusarAjuste() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { nota: string; contexto?: ContextoDoRetorno }>((_r, v) => ({
        titulo: 'Ajuste recusado',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: [...(v.contexto?.fatos ?? []), `nota: ${v.nota}`],
        efeito: 'Quem pediu vê a nota na lista de pedidos',
      })),
      silenciarFalha: true,
    },
    mutationFn: ({ id, nota }: { id: string; nota: string; contexto?: ContextoDoRetorno }) => api.recusarAjuste(id, nota),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useCancelarAjuste() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { contexto?: ContextoDoRetorno }>((_r, v) => ({
        titulo: 'Pedido de ajuste cancelado',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: v.contexto?.fatos ?? [],
      })),
    },
    mutationFn: ({ id }: { id: string; contexto?: ContextoDoRetorno }) => api.cancelarAjuste(id),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useExcluirMovimento() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { motivo: string; contexto?: ContextoDoRetorno }>((_r, v) => ({
        titulo: 'Lançamento excluído',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: [...(v.contexto?.fatos ?? []), `motivo: ${v.motivo}`],
        efeito: 'A linha e o motivo ficam no registro de exclusões',
      })),
      silenciarFalha: true,
    },
    mutationFn: ({ id, motivo }: { id: string; motivo: string; contexto?: ContextoDoRetorno }) => api.excluirMovimento(id, motivo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/* 0062 · as transferências em duas etapas. */
export function useTransferencias() {
  return useQuery({
    queryKey: [...CHAVE, 'transferencias'],
    queryFn: async () => (await api.transferencias()).transferencias,
  });
}

export function useRegistrarChegada() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { envio: Parameters<typeof api.registrarChegada>[1]; contexto?: ContextoDoRetorno }>((_r, v) => ({
        titulo: 'Chegada registrada',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: [`${litros(v.envio.quantidadeL)} chegaram`, ...(v.contexto?.fatos ?? [])],
        efeito: 'A transferência saiu de em trânsito',
      })),
      silenciarFalha: true,
    },
    mutationFn: ({ id, envio }: { id: string; envio: Parameters<typeof api.registrarChegada>[1]; contexto?: ContextoDoRetorno }) =>
      api.registrarChegada(id, envio),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useTransferirEntreTanques() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<
        Awaited<ReturnType<typeof api.transferir>>,
        Parameters<typeof api.transferir>[0] & { contexto?: ContextoDoRetorno }
      >((r, v) => ({
        titulo: 'Transferência registrada',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: [litros(v.quantidadeL), ...(v.contexto?.fatos ?? [])],
        efeito: r.situacao === 'concluida' ? 'Chegou junto: já está no tanque de destino' : 'Em trânsito até alguém registrar a chegada',
      })),
      silenciarFalha: true,
    },
    mutationFn: (v: Parameters<typeof api.transferir>[0] & { contexto?: ContextoDoRetorno }) => api.transferir(semContexto(v)),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useLancarCobranca() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, EnvioDeCobranca & { contexto?: ContextoDoRetorno }>((_r, v) => ({
        titulo: 'Cobrança do posto lançada',
        alvo: { codigo: v.documento, ...(v.contexto?.alvo?.nome ? { nome: v.contexto.alvo.nome } : {}) },
        fatos: [formatarReais(v.valorTotal), v.competencia ? `competência ${v.competencia}` : null],
      })),
      silenciarFalha: true,
    },
    mutationFn: (v: EnvioDeCobranca & { contexto?: ContextoDoRetorno }) => api.lancarCobranca(semContexto(v)),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useConciliarCobranca() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<Awaited<ReturnType<typeof api.conciliarCobranca>>, { contexto?: ContextoDoRetorno }>((r, v) => ({
        titulo: 'Cobrança conciliada',
        tom: r.diferenca === 0 ? 'sucesso' : 'ressalva',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: [`${formatarInteiro(r.ligados)} abastecimento${r.ligados === 1 ? '' : 's'} ligado${r.ligados === 1 ? '' : 's'}`],
        efeito: r.diferenca === 0 ? 'Bateu com o que foi lançado' : `Diferença de ${formatarReais(r.diferenca)} entre a cobrança e o lançado`,
      })),
    },
    mutationFn: ({ id, abastecimentos }: { id: string; abastecimentos: readonly string[]; contexto?: ContextoDoRetorno }) =>
      api.conciliarCobranca(id, abastecimentos),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useRegistrarLubrificante() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, EnvioDeLubrificante & { contexto?: ContextoDoRetorno }>((_r, v) => ({
        titulo: 'Aplicação de lubrificante registrada',
        ...(v.contexto?.alvo ? { alvo: v.contexto.alvo } : {}),
        fatos: [...(v.contexto?.fatos ?? []), formatarAteDuasCasas(v.quantidade), v.ponto ?? null],
        ...(v.depositoId ? { efeito: 'Baixou do estoque do depósito escolhido' } : {}),
      })),
      silenciarFalha: true,
    },
    mutationFn: (v: EnvioDeLubrificante & { contexto?: ContextoDoRetorno }) => api.registrarLubrificante(semContexto(v)),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/* ==========================================================================
 * O que a tela escreve quando a escrita não tem para onde ir
 * ======================================================================= */

/**
 * A mensagem de uma recusa, sempre em duas partes: o que houve e o que fazer.
 *
 * As duas frases são as do servidor quando ele as mandou. A tela não reescreve
 * regra de negócio — quem a conhece é quem a aplicou.
 */
export function explicarFalha(erro: unknown): { readonly erro: string; readonly comoResolver: string } {
  /* `SemSessao` NAO tem `comoResolver`, entao ela caia no generico — e o
     generico manda "tente de novo", que nunca ia funcionar: o que falta e
     entrar outra vez, e o formulario continua preenchido enquanto isso. */
  if (erro instanceof SemSessao) {
    return {
      erro: 'sua sessão expirou',
      comoResolver: 'Entre outra vez. O que você digitou continua na tela.',
    };
  }
  if (erro !== null && typeof erro === 'object' && 'comoResolver' in erro) {
    const recusa = erro as { message?: string; comoResolver?: string };
    return {
      erro: recusa.message ?? 'o pedido não foi aceito',
      comoResolver: recusa.comoResolver ?? 'Confira o que foi preenchido e tente outra vez.',
    };
  }
  /* "o lançamento não foi registrado" morava aqui, e esta função também
     responde por LISTA que não carregou — onde não houve lançamento nenhum e a
     frase descrevia outra coisa. A queda por situação mora em `api.ts`
     (`quedaDoErro`); aqui fica o caso de erro que não é nem recusa da API. */
  return {
    erro: 'não foi possível falar com o servidor',
    comoResolver: 'Tente de novo; se continuar, avise o suporte com o horário em que aconteceu.',
  };
}

/** Os tanques no formato curto que o formulário usa para escolher a origem. */
export function tanquesParaEscolha(tanques: readonly SaldoDeTanque[]): readonly Tanque[] {
  return tanques.map((t) => ({ id: t.id, nome: t.nome, precoLitro: t.precoLitro, padrao: t.padrao }));
}
